import React, {useEffect, useState} from "react";
import useDebounce from "../../../hooks/useDebounce";
import {useNavigate, useParams} from "react-router-dom";
import {FiSearch} from "react-icons/fi";
import Loader from "../../../components/common/Loader";
import InfiniteScroll from "react-infinite-scroll-component";
import CommentLoader from "../../../components/common/CommentLoader";
import MasterClassesList from "../../master-classes/components/MasterClassesList";
import notFound from "../../../assets/not-found/not_found.png";
import {TruncatedHtmlTextContent} from "../../../components/TruncatedHtmlTextContent/TruncatedHtmlTextContent";
import {playlistService} from "../../../services/playlistsService/playlistService";
import {PageErrorMessage} from "../../../components/PageErrorMessage";
import {MasterClassService} from "../../../services/masterClassService/masterClassService";
import CustomBreadcrumbs from "../../../components/CustomBreadcrumbs/CustomBreadcrumbs";
import {FaRegEdit} from "react-icons/fa";
import {MdDelete} from "react-icons/md";
import Swal from "sweetalert2";
import {communityService} from "../../../services/communityService/communityService";
import {toast} from "react-toastify";
import EditMemberDiscussionModal from "../../community/modals/EditMemberDiscussionModal";
import AddPlaylistModal from "../modals/AddPlaylistModal";

const PlaylistDetails = ({isFavorites}) => {
    const [masterClasses, setMasterClasses] = useState([]);
    const [playlistDetails, setPlaylistDetails] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 750);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const { id } = useParams();
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [showEditModal, setShowEditModal] = useState(false);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
        setIsLoading(true);
        setPage(1);
        getPlaylistMasterClasses(1, true);
    }, [debouncedSearchTerm]);

    const playlistDetailsData = async () => {
        setIsLoading(true);
        try {
            await playlistService.getSinglePlaylistData(id).then((response) => {
                setPlaylistDetails(response?.data);
            });

            setIsLoading(false);
        } catch (error) {
            setError({
                statusCode: error.response.status,
                message: error.response.data.message,
            });
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isFavorites) {
            playlistDetailsData();
        }
    }, [id]);

    const getPlaylistMasterClasses = async (currentPage = page, isNewSearch = false) => {
        try {
            const response = isFavorites
                ? await MasterClassService.getFavorites()
                : await playlistService.getPlaylistMasterclasses(id, currentPage, searchTerm);
            const newMasterClasses = response.data.data;

            if (isNewSearch) {
                setMasterClasses(newMasterClasses);
            } else {
                setMasterClasses(prevMasterClasses => [...prevMasterClasses, ...newMasterClasses]);
            }

            if (newMasterClasses.length === 0) {
                setHasMore(false);
            } else {
                setPage(currentPage + 1);
            }

            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    if (error) {
        return <PageErrorMessage
            error={error}
            entityTitle="Playlist"
        />;
    }

    const pages = [{
        title: 'Playlists',
        to: `/playlists`
    }];

    const handleDeleteConfirmation = () => {
        Swal.fire({
            title: "Are you sure?",
            text: "Are you sure you want to delete this playlist?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Delete",
        }).then((result) => {
            if (result.isConfirmed) {
                handleDelete();
            }
        });
    };

    const handleDelete = async () => {
        try {
            await playlistService.deletePlaylist(id);
            navigate('/playlists');
            toast.success('Playlist deleted successfully!');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete playlist');
        }
    };

    return (
        <>
            <div className="container-fluid">
                <CustomBreadcrumbs
                    currentPageTitle={isFavorites ? 'Favorites' : playlistDetails?.name}
                    pages={pages}
                />
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h3 className="d-lg-block">{isFavorites ? 'Favorites' : playlistDetails?.name}</h3>
                    <div className="search-container">
                        <input onChange={handleSearch} value={searchTerm} placeholder="Search"
                               className="search-input" autoFocus={true}/>
                        <FiSearch className="search-icon"/>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-1 mb-2">
                    <button className="border-0 p-0"
                            style={{background: "unset", color: "#198754"}}
                            onClick={() => setShowEditModal(true)}
                    >
                        <FaRegEdit fontSize={20}/>
                    </button>
                    <button className="border-0 p-0"
                            style={{background: "unset", color: "rgba(139, 0, 50, 1)"}}
                            onClick={handleDeleteConfirmation}
                    >
                        <MdDelete fontSize={24}/>
                    </button>
                </div>
                {isLoading ? (
                    <Loader />
                ) : (
                    <div className="masterclasses-scroll-container">
                        {masterClasses.length > 0 && (
                            <InfiniteScroll
                                next={() => getPlaylistMasterClasses(page)}
                                hasMore={hasMore}
                                loader={isLoading ? <CommentLoader/> : <></>}
                                dataLength={masterClasses.length}
                            >
                                <div className="masterclasses-scroll-content">
                                    <MasterClassesList masterClasses={masterClasses} />
                                </div>
                            </InfiniteScroll>
                        )}
                        {masterClasses?.length === 0 && (
                            <div className="w-100 mt-3 rounded-4 text-center d-flex flex-column align-items-center justify-content-center" style={{ height: "500px" }}>
                                <img crossOrigin="anonymous" src={notFound} alt="data not found" width={300} />
                                <h3>No masterclass found</h3>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {showEditModal &&
                <AddPlaylistModal
                    onClose={() => setShowEditModal(false)}
                    onUpdated={(playlist) => setPlaylistDetails(playlist)}
                    playlist={playlistDetails}
                />
            }
        </>
    );
};

export default PlaylistDetails;